// Based on https://github.com/snakesilk/react-fullscreen

// MIT License

// Copyright (c) 2017 Pontus Persson

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import fscreen from 'fscreen';

export interface FullScreenHandle {
  active: boolean;
  enter: () => void;
  exit: () => void;
  node: React.MutableRefObject<HTMLDivElement | null>;
}

export function useFullscreen(): FullScreenHandle {
  const [active, setActive] = useState<boolean>(false);
  const node = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleChange = () => {
      setActive(fscreen.fullscreenElement === node.current);
    };
    fscreen.addEventListener('fullscreenchange', handleChange);
    return () => fscreen.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const enter = useCallback(() => {
    if (fscreen.fullscreenElement) {
      fscreen.exitFullscreen();
      fscreen.requestFullscreen(node.current!);
    } else if (node.current) {
      fscreen.requestFullscreen(node.current);
    }
  }, []);

  const exit = useCallback(() => {
    if (fscreen.fullscreenElement === node.current) {
      fscreen.exitFullscreen();
    }
  }, []);

  return {
    active,
    enter,
    exit,
    node,
  };
}

export interface FullScreenProps {
  handle: FullScreenHandle;
  children: ReactNode;
  className?: string;
}

export const FullScreen = ({ handle, children, className }: FullScreenProps) => {
  const cls = classNames('fullscreen', handle.active && 'fullscreen-enabled', className);

  return (
    <div className={cls} ref={handle.node} style={handle.active ? { height: '100%', width: '100%' } : undefined}>
      {children}
    </div>
  );
};
